import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/Observable';
import { isDate, isNullOrUndefined } from 'util';
import { ConfirmationModalComponent } from '../../../shared/confirmation-modal/confirmation-modal.component';
import { DateUtil } from '../../../shared/utils/date.util';
import { PioneerValidators } from '../../../shared/utils/pioneer.validators';
import { OnyxEvent } from '../../models/event';
import { EventReservationSource } from '../../models/event-reservation-source';
import { Reservation } from '../../models/reservation';
import { ReservationStatusCode } from '../../models/reservation-status-code.enum';
import { ReservationCompareStore } from '../../service/reservation-compare-store';
import { ReservationStore } from '../../service/reservation-store';

@Component({
  selector: 'pioneer-reservation-form',
  templateUrl: './reservation-form.component.html',
  styleUrls: ['./reservation-form.component.css']
})
export class ReservationFormComponent implements OnInit {

  @ViewChild(ConfirmationModalComponent)
  addRatePlanCodeModal: ConfirmationModalComponent;

  @Input()
  event: OnyxEvent;

  reservationForm: FormGroup;
  currentReservation: Reservation;

  statusList;

  readonly addRatePlanCodeOption = 'addRatePlanCode';

  constructor(private formBuilder: FormBuilder,
              private translateService: TranslateService,
              private reservationStore: ReservationStore,
              private reservationCompareStore: ReservationCompareStore,
              public router: Router) { }

  ngOnInit() {

    if (!this.event) {
      throw new Error('The "event" attribute must be specified to use the ReservationFormComponent component');
    }
    this.initForm(null);
    this.initStatusList();
  }

  private initForm(reservation: Reservation) {
    this.reservationForm = this.formBuilder.group({
      confirmation: [reservation ? reservation.confirmation : null, [Validators.required, Validators.maxLength(32)]],
      firstName: [reservation ? reservation.firstName : null, [Validators.maxLength(50)]],
      lastName: [reservation ? reservation.lastName : null, [Validators.required, Validators.maxLength(50)]],
      checkInDate: [reservation ? reservation.checkInDate : null, [Validators.required, PioneerValidators.isDate]],
      checkOutDate: [reservation ? reservation.checkOutDate : null, [Validators.required, PioneerValidators.isDate]],
      status: [reservation ? reservation.status : null, [Validators.required]],
      roomType: [reservation ? reservation.roomType : null, [Validators.maxLength(6)]],
      roomRate: [reservation ? reservation.roomRate : null,
        [Validators.required, PioneerValidators.isValidAmountInRange(0.01, 99999999.99)]],
      ratePlanCode: [reservation ? reservation.ratePlanCode : null, [Validators.required]]
    }, {
        validator: PioneerValidators.isEndDateAfterStartDateCrossValidator('checkInDate', 'checkOutDate')
      }
    );

    this.ratePlanCode.valueChanges.subscribe(value => {
      if (value === this.addRatePlanCodeOption) {
        this.openAddRatePlanCodeModal();
      }
    });
  }

  public init(reservation: Reservation) {
    this.initForm(reservation);
    this.currentReservation = reservation;
  }

  private initStatusList() {
    const i18nKey = 'RESERVATION_STATUS.';
    this.translateService.get([
      i18nKey + 'RESERVED',
      i18nKey + 'CHECKED_IN',
      i18nKey + 'CHECKED_OUT',
      i18nKey + 'CANCELLED',
      i18nKey + 'NO_SHOW'
    ]).subscribe(i18nTexts => {
      this.statusList = [
        { key: ReservationStatusCode.RESERVED, value: i18nTexts[i18nKey + 'RESERVED'] },
        { key: ReservationStatusCode.CHECKED_IN, value: i18nTexts[i18nKey + 'CHECKED_IN'] },
        { key: ReservationStatusCode.CHECKED_OUT, value: i18nTexts[i18nKey + 'CHECKED_OUT'] },
        { key: ReservationStatusCode.CANCELLED, value: i18nTexts[i18nKey + 'CANCELLED'] },
        { key: ReservationStatusCode.NO_SHOW, value: i18nTexts[i18nKey + 'NO_SHOW'] }
      ];
    });
  }

  get checkInDate(): Date { return this.parseDate(this.reservationForm.get('checkInDate').value); }
  get checkOutDate(): Date { return this.parseDate(this.reservationForm.get('checkOutDate').value); }
  get ratePlanCode() { return this.reservationForm.get('ratePlanCode'); }

  private parseDate(value: string): Date {
    if (isNullOrUndefined(value) || value.trim().length === 0) {
      return null;
    }
    return new Date(value);
  }

  get nights(): number {
    if (!isDate(this.checkInDate) || !isDate(this.checkOutDate)) {
      return 0;
    }
    if (this.checkInDate >= this.checkOutDate) {
      return 0;
    }
    return DateUtil.differenceInCalendarDays(this.checkOutDate, this.checkInDate);
  }

  get roomRate() {
    const value = this.reservationForm.get('roomRate').value;
    if (isNullOrUndefined(value)) {
      return null;
    }
    return value;
  }

  private resetForm(): void {
    this.reservationForm.reset({
      confirmation: {value: null, disabled: false},
      firstName: {value: null, disabled: false},
      lastName: {value: null, disabled: false},
      checkInDate: {value: null, disabled: false},
      checkOutDate: {value: null, disabled: false},
      status: {value: null, disabled: false},
      roomType: {value: null, disabled: false},
      roomRate: {value: null, disabled: false},
    });
  }

  addReservation(source: EventReservationSource): Observable<any> {
    this.reservationForm.disable();

    const reservation = this.createOrUpdateReservation(null);

    const obs = new Observable((observer) => {
      this.reservationStore.addReservation(reservation, source)
        .finally(() => {
          this.reservationForm.enable();
        })
        .catch((error) => {
          observer.error();
          return Observable.throw(error);
        })
        .subscribe(() => {
          // Refresh reservations or RSVPs depending on the source
          switch (source) {
            case EventReservationSource.MANUAL:
              this.reservationCompareStore.refreshCurrentReservationList(this.event.eventId);
              break;
            case EventReservationSource.RSVP:
              this.reservationCompareStore.refreshCurrentRsvpList(this.event.eventId);
              break;
            default:
              break;
          }
          this.resetForm();
          observer.next();
        });
    });

    return obs;
  }

  cancelAddReservation() {
    this.resetForm();
  }

  updateReservation(eventReservationId: number): Observable<any> {
    this.reservationForm.disable();

    const reservation = this.createOrUpdateReservation(eventReservationId);

    const obs = new Observable((observer) => {
      this.reservationStore.updateReservation(reservation)
        .finally(() => {
          this.reservationForm.enable();
        })
        .catch((error) => {
          observer.error();
          return Observable.throw(error);
        })
        .subscribe(() => {
          this.reservationCompareStore.refreshCurrentReservationList(this.event.eventId);
          this.resetForm();
          observer.next();
        });
    });

    return obs;
  }

  private createOrUpdateReservation(eventReservationId: number) {
    return new Reservation({
      eventReservationId: eventReservationId,
      confirmation: this.reservationForm.get('confirmation').value,
      firstName: this.reservationForm.get('firstName').value,
      lastName: this.reservationForm.get('lastName').value,
      checkInDate: this.checkInDate,
      checkOutDate: this.checkOutDate,
      nights: this.nights,
      roomType: this.reservationForm.get('roomType').value,
      ratePlanCode: this.ratePlanCode.value,
      roomRate: this.roomRate,
      status: this.reservationForm.get('status').value,
      eventId: this.event.eventId
    });
  }

  openAddRatePlanCodeModal() {
    this.addRatePlanCodeModal.open();

    if (this.currentReservation && this.currentReservation.ratePlanCode) {
      this.ratePlanCode.setValue(this.currentReservation.ratePlanCode);
    } else {
      this.ratePlanCode.reset(null, { emitEvent: false });
    }
  }

  addRatePlanCodeModalHandler(addRatePlanCode: boolean) {
    if (addRatePlanCode) {
      this.router.navigate(['event/' + this.event.eventId + '/summary/edit']);
    }
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/reservations/reservation-form/reservation-form.component.ts